import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { useFullscreenModal } from '../../../../models/hooks/appStateHooks';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { TextRegistrationRequiredModal } from '../../../components/texting/TextRegistrationRequiredModal';
import { bs } from '../../../styles/styles';
import { useTextingCampaignRegistrationContext } from '../../Texting/TextingCampaignRegistrationPrompt/context';
import { useTextingRegistrationStatus } from '../../TextingRegistration/TextingRegistrationForm/hooks/useTextingRegistrationStatus';
import { styleSheet } from './styles';
import { TextingCampaignBrowserCards } from './TextingCampaignBrowserCards';
import { TextingCampaignDraftBrowserCards } from './TextingCampaignDraftBrowserCards';

type TextingTabMode = 'templates' | 'drafts';

export const TextingTabPage: React.FC<{
	setTabListAccessory: (node: React.ReactNode) => void;
}> = ({ setTabListAccessory }) => {
	const {
		showRegistrationRequiredModal,
		setShowRegistrationRequiredModal,
		isLoading,
		textSettings,
		checkShowRegistration,
	} = useTextingCampaignRegistrationContext();

	const [selectedTab, setSelectedTab] = React.useState<TextingTabMode>('templates');

	// eslint-disable-next-line react-hooks/exhaustive-deps
	React.useEffect(checkShowRegistration, []);

	React.useEffect(() => {
		setTabListAccessory(<TextingTabRegistrationStatus />);
		return () => {
			setTabListAccessory(null);
		};
	}, [setTabListAccessory]);

	const fullscreenModal = useFullscreenModal();
	const history = useHistory();

	const onCloseRegistrationWarningClicked = () => {
		setShowRegistrationRequiredModal(false);
		history.push('/campaigns/emails');
	};

	return isLoading ? (
		<div className={css(styleSheet.grayBackground, bs.wFull, bs.boxBorder, bs.hFull, styleSheet.paddingTop)}>
			<LoadingSpinner type='small' />
		</div>
	) : (
		<div className={css(styleSheet.grayBackground, bs.wFull, bs.boxBorder, bs.hFull)}>
			<div className={css(bs.flex, bs.justifyBetween, bs.itemsCenter, bs.px6, bs.pt5, bs.pb2, bs.boxBorder)}>
				<div className={css(bs.flex)}>
					<button
						className={css(styleSheet.tabButton, selectedTab === 'templates' && styleSheet.tabButtonActive)}
						onClick={() => setSelectedTab('templates')}
					>
						Templates
					</button>
					<button
						className={css(styleSheet.tabButton, selectedTab === 'drafts' && styleSheet.tabButtonActive)}
						onClick={() => setSelectedTab('drafts')}
					>
						Drafts
					</button>
				</div>
				<button className={css(bs.ctaButton)} onClick={() => fullscreenModal.history.push('/texting-campaign/search')}>
					Create New Text Campaign
				</button>
			</div>
			{selectedTab === 'templates' ? (
				<TextingCampaignBrowserCards enabled={selectedTab === 'templates'} />
			) : (
				<TextingCampaignDraftBrowserCards enabled={selectedTab === 'drafts'} />
			)}
			<TextRegistrationRequiredModal
				showRegistrationRequiredModal={showRegistrationRequiredModal}
				telephonyTextSettings={textSettings}
				onRequestClose={onCloseRegistrationWarningClicked}
			/>
		</div>
	);
};

function TextingTabRegistrationStatus() {
	const textingRegistrationStatus = useTextingRegistrationStatus();
	if (textingRegistrationStatus.isLoading) {
		return null;
	}
	if (textingRegistrationStatus.brandStatus == null) {
		return (
			<div className={css(styleSheet.statusColumn, styleSheet.columnLayout)}>
				<p className={css(styleSheet.statusText)}>Ready to send text campaigns?</p>
				<Link className={css(styleSheet.linkText)} to='/texting/registration'>
					Get registered today &rarr;
				</Link>
			</div>
		);
	}
	if (
		textingRegistrationStatus.brandStatus === Api.TextingBrandStatus.Pending ||
		textingRegistrationStatus.campaignStatus === Api.CampaignRegistrationStatus.Pending
	) {
		return (
			<p className={css(styleSheet.statusText)}>
				Registration Status: <span className={css(styleSheet.pendingText)}>Processing</span>
			</p>
		);
	}
	if (
		textingRegistrationStatus.brandStatus === Api.TextingBrandStatus.Failed ||
		textingRegistrationStatus.campaignStatus === Api.CampaignRegistrationStatus.Failed ||
		textingRegistrationStatus.brandStatus === Api.TextingBrandStatus.Unverified ||
		textingRegistrationStatus.campaignStatus === Api.CampaignRegistrationStatus.Declined
	) {
		return (
			<div className={css(styleSheet.statusColumn, styleSheet.columnLayout)}>
				<p className={css(styleSheet.statusText)}>
					Registration Status: <span className={css(styleSheet.errorText)}>Declined</span>
				</p>
				<p className={css(styleSheet.statusText)}>Please reach out to your CSM</p>
			</div>
		);
	}
	if (textingRegistrationStatus.campaignStatus === 'Accepted') {
		return (
			<p className={css(styleSheet.statusText)}>
				Registration Status: <span className={css(styleSheet.successText)}>Accepted</span>
			</p>
		);
	}
	return null;
}
